import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Nav from "@/components/Nav";
import WorkCard from "@/components/WorkCard";
import CharacterCard from "@/components/CharacterCard";
import DonateModal from "@/components/DonateModal";
import FollowButton from "@/components/FollowButton";
import api from "@/lib/api";
import { Feather, BookOpen, ArrowLeft, Swords } from "lucide-react";

export default function Author() {
  const { pseudonym } = useParams();
  const [data, setData] = useState(null);
  const [donateWork, setDonateWork] = useState(null);

  useEffect(() => {
    api.get(`/authors/${encodeURIComponent(pseudonym)}`).then(({ data }) => setData(data)).catch(() => setData({ error: true }));
  }, [pseudonym]);

  if (!data) return <div className="min-h-screen grain"><Nav /></div>;
  if (data.error) return (
    <div className="min-h-screen grain">
      <Nav />
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-24 text-center">
        <h1 className="font-serif-display text-4xl text-[#F7F3EC] mb-3">Pen name not found.</h1>
        <Link to="/browse" className="text-[#D9B472]" data-testid="author-notfound-back">Back to archive →</Link>
      </div>
    </div>
  );

  const { author, works, characters = [] } = data;
  const totalWorks = works.length;
  const totalChars = characters.length;
  const avgCompletion = totalWorks ? Math.round(works.reduce((s, w) => s + w.completion_percent, 0) / totalWorks) : 0;

  return (
    <div className="min-h-screen grain">
      <Nav />
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10 sm:py-14">
        <Link to="/browse" className="flex items-center gap-2 text-[#6E675E] hover:text-[#D9B472] font-mono-brass text-xs uppercase tracking-widest mb-8 transition-colors duration-200" data-testid="author-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to archive
        </Link>

        <header className="gold-frame paper-scroll rounded-sm p-8 sm:p-12 mb-12 relative overflow-hidden" data-testid="author-header">
          <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{background: "radial-gradient(circle at 20% 30%, #D9B472, transparent 50%)"}} />
          <div className="relative">
            <div className="flex flex-wrap items-center gap-4 mb-4">
              <div className="flex items-center gap-2">
                <Feather className="w-4 h-4 text-[#D9B472]" />
                <span className="text-[10px] uppercase tracking-widest text-[#D9B472] font-mono-brass">Anonymous author · pen name only</span>
              </div>
              <FollowButton pseudonym={author.pseudonym} />
            </div>
            <h1 className="font-serif-display italic text-5xl sm:text-7xl text-[#F7F3EC] tracking-tight mb-4" data-testid="author-pseudonym">
              {author.pseudonym}
            </h1>
            {author.bio && <p className="font-serif-body text-lg text-[#A8A095] max-w-2xl leading-relaxed mb-6 italic">"{author.bio}"</p>}
            <div className="flex flex-wrap gap-6 pt-6 border-t border-[rgba(217,180,114,0.12)] text-sm">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#6E675E] font-mono-brass mb-0.5">Fragments</div>
                <div className="font-mono-brass text-[#F7F3EC] text-lg">{totalWorks}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#6E675E] font-mono-brass mb-0.5">Characters</div>
                <div className="font-mono-brass text-[#F7F3EC] text-lg">{totalChars}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#6E675E] font-mono-brass mb-0.5">Avg completion</div>
                <div className="font-mono-brass text-[#F7F3EC] text-lg">{avgCompletion}%</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#6E675E] font-mono-brass mb-0.5">Enrolled since</div>
                <div className="font-mono-brass text-[#F7F3EC] text-lg">{new Date(author.created_at).getFullYear()}</div>
              </div>
            </div>
          </div>
        </header>

        <h2 className="font-serif-display text-2xl sm:text-3xl text-[#F7F3EC] mb-6 flex items-center gap-3">
          <BookOpen className="w-5 h-5 text-[#D9B472]" /> Every unfinished thing they've offered
        </h2>

        {works.length === 0 ? (
          <p className="text-[#6E675E] font-serif-body italic">No fragments listed yet.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8" data-testid="author-works-grid">
            {works.map((w, i) => <WorkCard key={w.id} work={w} onDonate={setDonateWork} index={i} />)}
          </div>
        )}

        {characters.length > 0 && (
          <>
            <h2 className="font-serif-display text-2xl sm:text-3xl text-[#F7F3EC] mt-16 mb-6 flex items-center gap-3">
              <Swords className="w-5 h-5 text-[#D9B472]" /> Characters from the Vault
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 lg:gap-8" data-testid="author-characters-grid">
              {characters.map((c, i) => <CharacterCard key={c.id} character={c} index={i} />)}
            </div>
          </>
        )}
      </div>
      {donateWork && <DonateModal work={donateWork} onClose={() => setDonateWork(null)} />}
    </div>
  );
}
